import type Stripe from 'stripe';
import { ChatbotAPI } from '../api/ChatbotAPI';
import { createReportingAPIs } from '../api/impl/createReportingAPIs';
import type { InstagramClient } from '../api/impl/InstagramClient';
import { LineMessageClient } from '../api/impl/LineMessageClient';
import type { RichMenuAPIClient } from '../api/impl/LineRichMenuAPIClient';
import {
    createPlatformChatbotAPI,
    defaultValidateChatbotSubscription,
    PostMessageToChatbotFn,
} from '../api/impl/PlatformChatbotAPI';
import { createScheduledActionsAPI } from '../api/impl/ScheduledActionsAPI';
import { createStubChatbotAPI } from '../api/impl/StubChatbotAPI';
import { Fetcher } from '../api/impl/utils/Fetcher';
import { updatePaymentSubscriptionStatusIfExpired } from '../api/impl/utils/paymentUtils';
import type { KeyValueStoreAPI, KeyValueStoreSendOpts } from '../api/KeyValueStoreAPI';
import type {
    ABTestAssignments,
    DelayedAction,
    ReplicantAsyncActionAPI,
    ReplicantEventHandlerAPI,
    ReplicantPlatformMessagePayloadEncoderAPI,
    SharedStateDelayedAction,
} from '../api/ReplicantAPI';
import { ScheduledActionsAPI } from '../api/ScheduledActionsAPI';
import { AnalyticsEvent, AnalyticsPayload, PostAnalyticsFN } from '../common/Analytics';
import { ErrorReporter, mockErrorReporter } from '../common/ErrorReporter';
import { FriendsStatesMap } from '../common/FriendsStatesMap';
import { Geolocale } from '../common/Geolocale';
import { LoginInfo, LoginInfoWebPlayer } from '../common/LoginInfo';
import type { OTPRequestResponse, OTPVerificationOpts } from '../common/OTP';
import {
    APNProps,
    APNRawPayload,
    FCMProps,
    PushNotificationPlatformProps,
    PushNotificationPlatformResponses,
    PushNotificationResponse,
} from '../common/PushNotifications';
import { type ABTestsDynamicConfig, calculateAssignmentChanges } from '../common/ReplicantABTests';
import { APPLY_MESSAGES_ACTION, KV_MESSENGER_ID_PREFIX } from '../common/ReplicantConstants';
import type { Gender, OAuthResponse, UserMessageItem } from '../common/Types';
import DB, {
    ChangesetReplication,
    ChatbotMetainfo,
    ConditionalWriteError,
    Entry,
    hasMigratedLegacyUserSharedStateItems,
} from '../db/DB';
import { createIndexDocTooLargeWarning, ReplicantError, ReplicantErrorCode, ReplicantErrorSubCode } from '../Errors';
import type { IndexDB, SharedStateIndexDBs } from '../indexdb/IndexDB';
import { IndexingQueue } from '../indexingQueue';
import logger from '../logger';
import { Actions } from '../ReplicantActions';
import type {
    ChatbotMessageInstance,
    LineWebhookEvent,
    TemplateRendererAPI,
    ZoomWebhookEvent,
} from '../ReplicantChatbot';
import { generateIndexDoc } from '../ReplicantComputedProperties';
import { Replicant, ReplicantConfig, validateConfig } from '../ReplicantConfig';
import { applyEventHandlerMessage } from '../ReplicantEventHandlerMessages';
import type { IndexCountFn, IndexSearchFn, QueryOpts, QueryResults, SearchOpts } from '../ReplicantIndexes';
import type { MessageItem, Messages } from '../ReplicantMessages';
import type { ReplicantModule } from '../ReplicantModules';
import { ReplicantPlatform } from '../ReplicantPlatform';
import type { Ruleset } from '../ReplicantRuleset';
import { sharedStateHasSearchableProperties, type SharedStates } from '../ReplicantSharedStates';
import { SocialGraphInteraction } from '../ReplicantSocialGraph';
import { ScheduledActionsQueue } from '../scheduledActionsQueue';
import SB from '../SchemaBuilder';
import type { AtoneWebhookHandler, CreateAtoneWebhookHandler } from '../server/webhooks/createAtoneWebhookHandler';
import { addNonEnumerableSystemStateFields, getSystemStateFieldsFromEntry, WithMeta } from '../systemStateFields';
import type { AirtableClient } from '../utils/Airtable';
import { getAppName } from '../utils/AppName';
import { delay } from '../utils/AsyncUtils';
import { isAndroidDeviceTokenExpired, isAppleDeviceTokenExpired } from '../utils/DeviceTokenUtils';
import { isAnonymousWebPlayer } from '../utils/isAnonymousWebPlayer';
import {
    getApplicableMessages,
    getUnreducedMessagesForDeletion,
    isEventHandlerMessage,
    isMessageRecentlyReduced,
    updateRecentlyReducedMessages,
    warnOnOversizeMessages,
} from '../utils/MessageUtils';
import { deepMerge, getNamespacedValue } from '../utils/ObjUtils';
import type { TwilioClient } from '../utils/Twilio';
import { duration, generateRandomId, unreachable } from '../utils/Utils';
import { createVirtualEntry, UnknownEntry, UnknownState, VirtualEntry } from '../utils/VirtualState';
import type { WebPushClient } from '../utils/WebPush';
import type { AIClient } from './AIClient';
import type { AtoneClient } from './Atone';
import { LineOAuthClient } from './auth/LineOAuthClient';
import ServerReplicantOAuth from './auth/ServerReplicantOAuth';
import type { DiscordClient } from './Discord';
import type { KomojuClient } from './Komoju';
import Metrics from './Metrics';
import type { NarrowcastAPIClient } from './NarrowcastAPIClient';
import * as perf from './performanceTracking';
import { resolveActionScheduleUpdates } from './resolveActionScheduleUpdates';
import { SendTemplatedEmail } from './Sendgrid';
import ServerAssetUploader from './ServerAssetUploader';
import ServerReplicantABTests from './ServerReplicantABTests';
import ServerReplicantAdmin from './ServerReplicantAdmin';
import { ServerReplicantAirtable } from './ServerReplicantAirtable';
import { ServerReplicantAnalytics } from './ServerReplicantAnalytics';
import ServerReplicantAndroidPushNotifications from './ServerReplicantAndroidPushNotifications';
import { PostAPNFn } from './ServerReplicantAPN';
import { ServerReplicantAsyncGetters } from './ServerReplicantAsyncGetters';
import ServerReplicantChatbot, {
    ON_GAME_END_NOTIFICATION_ID,
    SendChatbotMessageFn,
    ServerReplicantChatbotOptions,
} from './ServerReplicantChatbot';
import ServerReplicantCreateOrLogin from './ServerReplicantCreateOrLogin';
import { ServerReplicantECPM } from './ServerReplicantECPM';
import ServerReplicantExchangeRate from './ServerReplicantExchangeRate';
import type { PostFCMFn } from './ServerReplicantFCM';
import ServerReplicantFetchStates from './ServerReplicantFetchStates';
import ServerReplicantInternalKeyValueStore from './ServerReplicantInternalKeyValueStore';
import ServerReplicantIOSPushNotifications from './ServerReplicantIOSPushNotifications';
import ServerReplicantKeyValueStore from './ServerReplicantKeyValueStore';
import { ServerReplicantLoginTokens } from './ServerReplicantLoginTokens';
import { InitiateOtpOpts, ServerReplicantOTP } from './ServerReplicantOTP';
import ServerReplicantReplicate, { ReplicationResult } from './ServerReplicantReplicate';
import ServerReplicantScheduledActions from './ServerReplicantScheduledActions';
import ServerReplicantSharedStates, { SharedStateIndexDocs } from './ServerReplicantSharedStates';
import { ServerReplicantSocialGraph } from './ServerReplicantSocialGraph';
import { ServerReplicantSystemScheduledActions } from './ServerReplicantSystemScheduledActions';
import { ServerReplicantUserIDEncryption } from './ServerReplicantUserIDEncryption';
import { Diff, SnapshotUpdate, updateSnapshotTimestamps } from './StateRecovery';
import type { StripeClient } from './Stripe';
import { TaskRunnerClient } from './TaskRunner';
import type { TelegramClient } from './Telegram';
import { updateABTestAssignments } from './updateABTestAssignments';
import type { CryptoUtils } from './utils/CryptoUtils';
import { withIndexDocGeneration } from './utils/IndexUtils';
import { revertMutationIfThrows } from './utils/JSONPatchUtils';
import createFBWebhookHandler, { FBWebhookEvent } from './webhooks/createFBWebhookHandler';
import type { CreateKomojuWebhookHandler, KomojuWebhookHandler } from './webhooks/createKomojuWebhookHandler';
import { CreateStripeWebhookHandler, StripeWebhookHandler } from './webhooks/createStripeWebhookHandler';
import type { CreateXsollaWebhookHandler, XsollaWebhookHandler } from './webhooks/createXsollaWebhookHandler';
import { InstagramWebhookHandler } from './webhooks/InstagramWebhookHandler';
import { LineWebhookHandler } from './webhooks/LineWebhookHandler';
import { MessengerWebhookHandler } from './webhooks/MessengerWebhookHandler';
import { createTelegramWebhookHandler, TelegramWebhookHandler } from './webhooks/TelegramWebhookHandler';
import { ZoomWebhookHandler } from './webhooks/ZoomWebhookHandler';
import type { XsollaClient } from './Xsolla';

export type ExecutionContext = {
    entryPromises: { [userId: string]: Promise<Entry<WithMeta<any, any, any>> | null> };
    performanceTracking?: { context?: perf.PerformanceContext };
};

export type PushDocsToIndexFn = (docs: { [userId: string]: { [key: string]: unknown } }) => Promise<void>;

export type PushSharedStateDocToIndexFn = (opts: {
    indexDoc: { [key: string]: unknown };
    stateName: string;
    stateId: string;
}) => Promise<void>;

export type ReplicationEntry = {
    fn: string;
    args?: unknown;
    async: boolean;
    meta?: { now: number };
    isServerSide?: boolean;
    times?: number;
};

export type ResolveDelayedActionsFn = (delayedActions: DelayedAction[], context?: ExecutionContext) => Promise<void>;

type WithMetainfo<T> = {
    data: T;
    t: number;
    service: number;
};

type Token = { token: string };

export type CreateOrLoginOutput<TUserState> = WithMetainfo<Entry<TUserState> & Token & LoginInfo>;
export type CreateOrLoginWebPlayerOutput<TUserState> = WithMetainfo<Entry<TUserState> & Token & LoginInfoWebPlayer>;
export type GetTokenOutput = WithMetainfo<Token>;

export type ReplicateOutput<TUserState, TRuleset extends Ruleset> = WithMetainfo<
    ReplicationResult<TUserState, TRuleset>
>;

export type FetchStatesResults<TUserState, TRuleset extends Ruleset, TSharedStates extends SharedStates = {}> = {
    states: FriendsStatesMap<TUserState, TRuleset, TSharedStates>;
    unprocessedIds?: string[];
};

export type FetchStatesOutput<TUserState, TRuleset extends Ruleset> = WithMetainfo<
    FetchStatesResults<TUserState, TRuleset>
>;

export type UserAssetUploadOutput = WithMetainfo<{ assetUrl: string }>;

export type InferGenderFromNameOutput = WithMetainfo<Gender>;
export type KvPairOutput = WithMetainfo<{ [key: string]: string }>;
export type WriteKvPairsOutput = WithMetainfo<boolean>;

/** @returns Promise that resolves to `true` if user is subscribed to the platform chatbot. */
export type ValidateChatbotSubFn = (opts: {
    sendChatbotMessage: SendChatbotMessageFn;
    sendAnalyticsEvents: PostAnalyticsFN;
    entry: Entry<unknown> | null;
    userId: string;
    invokeTime: number;
    db: DB<any>;
    isDev: boolean;
    fetcher: Fetcher;
    messageToSend?: ChatbotMessageInstance;
}) => Promise<boolean>;

export type WriteChangesetFN = (opts: {
    id: string;
    entry: Entry<any>;
    replication: ChangesetReplication;
    messages: UserMessageItem[];
    date?: Date;

    /** Difference between pre- and post-replication states represented as JSON-Patch operations. */
    diff: Diff;

    /** Snapshot update operations to be performed alongside changeset write. */
    snapshotUpdate: SnapshotUpdate;
}) => Promise<void>;

export type PushNotificationOutput = WithMetainfo<PushNotificationResponse>;

type PushNotificationPlatformsOutput = WithMetainfo<PushNotificationPlatformResponses>;

export type SetTestUsersOutput = WithMetainfo<{ testUsersMap: { [testUserId: string]: string } }>;

export type ServerReplicantDevOptions = {
    simulatedLatency: number;
    dateNow: () => number;
    inferGenderFromName: (name: string) => Promise<Gender>;
    lookupGeoIp: (ip: string) => Geolocale | undefined;

    // Chatbot related
    chatbotAssetUrls: { [id: string]: string };
    userAssetsBaseUrl: string;

    postFBMessage: PostMessageToChatbotFn;
    postLineMessage: PostMessageToChatbotFn;
    postZoomMessage: PostMessageToChatbotFn;
    validateChatbotSubscription: ValidateChatbotSubFn;
    postAnalytics: PostAnalyticsFN;
    postActivePushNotification: PostAPNFn;
    postFirebaseCloudMessage: PostFCMFn;
    postNativeBridgeSecret: PostNativeBridgeSecretFn;
    richMenuAPIClient?: RichMenuAPIClient;

    /**
     * Reduce messages to user state on message post once the number of unreduced messages reaches this threshold.
     *
     * @default 100
     */
    reduceMessagesMinThreshold: number;

    /**
     * Reduce messages to user state on message post if the receiver has been inactive for this duration or longer.
     *
     * @default 1 minute
     */
    reduceMessagesMinInactiveMs: number;

    disableExchangeRatesLocalCache: boolean;
    abTestsCacheExpiresInMs: number;
    sendTemplatedEmail: SendTemplatedEmail;

    errorReporter?: ErrorReporter;
    metrics?: Metrics;

    createAtoneWebhookHandler?: CreateAtoneWebhookHandler;
    createKomojuWebhookHandler?: CreateKomojuWebhookHandler;
    createXsollaWebhookHandler?: CreateXsollaWebhookHandler;
    createStripeWebhookHandler?: CreateStripeWebhookHandler;
};

type SearchSharedStatesFn = (sharedStateName: string, query: SearchOpts<any>) => Promise<QueryResults<any>>;
type CountSharedStatesFn = (sharedStateName: string, query: QueryOpts<any>) => Promise<number>;

export type APIAccessInterface<T extends Replicant> = {
    abTests: Pick<ServerReplicantABTests, 'getDynamicConfig'>;
    airtable: ServerReplicantAirtable;
    assetUploader: ServerAssetUploader;
    asyncGetters: ServerReplicantAsyncGetters<T>;
    config: ReplicantConfig<T>;
    countPlayers: IndexCountFn<any>;
    countSharedStates: CountSharedStatesFn;
    createChatbotRendererApi: CreateChatbotRendererAPIFn;
    createEventHandlerApi: CreateEventHandlerAPIFn<T>;
    dateNow: () => number;
    exchangeRateHandler: ServerReplicantExchangeRate;
    fetchStates: (
        ids: string[],
        opts: { consistentFetchIds: string[]; disablePagination?: boolean },
    ) => Promise<FetchStatesResults<T['state'], T['ruleset'], T['sharedStates']>>;
    getReadOnlyEntry: GetReadOnlyEntryFn<T>;
    internalKvStore: KeyValueStoreAPI;
    isInitialDevelopment: () => boolean;
    kvStore: KeyValueStoreAPI;
    loginTokens: ServerReplicantLoginTokens;
    otp: Pick<ServerReplicantOTP, 'verifyAndLinkUserId'>;
    platform: ReplicantPlatform;
    postMessages: PostMessagesFn;
    reportError: (e: Error, extraContext?: { [key: string]: any }) => Promise<void>;
    resolveDelayedActions: ResolveDelayedActionsFn;
    searchPlayers: IndexSearchFn<any>;
    searchSharedStates: SearchSharedStatesFn;
    sendAnalyticsEvents: (events: AnalyticsEvent[]) => Promise<void>;
    setNativeBridgeSecret: (userId: string, bridgeSecret: string) => Promise<void>;
    sharedStates: ServerReplicantSharedStates;
    socialGraph: ServerReplicantSocialGraph;
    systemScheduledActions: ServerReplicantSystemScheduledActions;
    writeMessages: (receiverId: string, messages: MessageItem[]) => Promise<void>;
};

export type CreateActionAPIFn<T extends Replicant> = (opts: {
    abTestsDynamicConfig: ABTestsDynamicConfig;
    async: boolean;
    consistentFetchIds: string[];
    delayedActions: DelayedAction[];
    entry: Entry<T['state']>;
    invokeTime: number;
    sessionId?: string;
    userId: string;
    fetcher: Fetcher;
    isServerSide: boolean;
}) => ReplicantAsyncActionAPI<T>;

export type CreateChatbotRendererAPIFn = (opts: { receiverId: string; senderId: string }) => TemplateRendererAPI;

export type CreateEventHandlerAPIFn<T extends Replicant> = (opts: {
    consistentFetchIds: string[];
    delayedActions: DelayedAction[];
    entry: Entry<T['state']>;
    userId: string;
    scheduledActionId?: string;
}) => Promise<ReplicantEventHandlerAPI<T>>;

export type CreateServerReplicantActionAPIOpts<T extends Replicant> = {
    abTestsDynamicConfig: ABTestsDynamicConfig;
    apiAccess: APIAccessInterface<T>;
    id: string;
    komojuClient?: KomojuClient;
    telegramClient?: TelegramClient;
    xsollaClient?: XsollaClient;
    sessionId?: string;
    entry: Entry<T['state']>;
    delayedActions: DelayedAction[];
    invokeTime: number;
    db: DB<T['state']>;
    encryptUserId: (userId: string) => Promise<string>;
    platform: ReplicantPlatform;
    async?: boolean;
    messages?: T['messages'];
    sharedStates: T['sharedStates'] | undefined;
    scheduledActionsApi: ScheduledActionsAPI<T['scheduledActions']>;
    chatbotApi: ChatbotAPI;
    consistentFetchIds: string[];
    userAssetsBaseUrl: string;
    sendTemplatedEmail: SendTemplatedEmail;
    fetcher: Fetcher;
};

export type GetReadOnlyEntryFn<T extends Replicant> = (
    userId: string,
    opts?: {
        assignABTests?: boolean;
        consistentRead?: boolean;
        createIfNotFound?: boolean;

        /** @default false */
        reduceMessages?: boolean;

        skipWrites?: boolean;
    },
) => Promise<{
    consumedCapacity: number;
    entry: Entry<WithMeta<T['state'], T['ruleset'], T['sharedStates']>> & { lastAppliedMessageId?: string };
    isNewUser: boolean;
    messagesCount: number;
} | null>;

export type ApplyMessagesToStateFn = (
    userId: string,
    entry: Entry<any>,
    messages: UserMessageItem[],
    opts?: { isSampled?: boolean },
) => {
    reducedMessages: UserMessageItem[];
    unreducedMessages: UserMessageItem[];
};

export type MigrateAndApplyMessagesFn = (
    userId: string,
    entry: UnknownEntry | VirtualEntry<any>,
    messages: UserMessageItem[],
    opts?: { abTestAssignments?: ABTestAssignments; isSampled?: boolean },
) => {
    virtual: VirtualEntry<any>;
    reducedMessages: UserMessageItem[];
    unreducedMessages: UserMessageItem[];
};

export type PostMessagesFn = (opts: {
    receiverId: string;
    messages: {
        id: string;
        name: string;
        args: any;
        sender: string;
        senderClockOffset: number;
        skipDbWrite?: boolean;
    }[];
    context?: ExecutionContext;
}) => Promise<{ [userId: string]: { [key: string]: unknown } }>;

export type PostNativeBridgeSecretFn = (opts: {
    bridgeSecret: string;
    playerId: string;
    referralCode: string;
}) => Promise<void>;

export class ServerReplicant<T extends Replicant> {
    /** Invoked by replicant-gcinstant-extensions, only on the Facebook platform. */
    handleAfterContextSwitchHook: (opts: { contextSwitchEventId: string; id: string }) => Promise<WithMetainfo<void>> =
        this.handler(async (opts) => {
            if (this.platform !== 'fb') {
                return;
            }

            // Mark the onGameEnd event as cancelled so that when the FB context switch webhook reaches Replicant we don't
            // invoke the onGameEnd event handler before game end (see https://playco.atlassian.net/browse/CORE-1073 for details):
            const metainfoMVCC = await this.db.metainfoMVCC.addCancelledOnGameEndEventId(
                opts.id,
                opts.contextSwitchEventId,
            );

            // Also unschedule any already scheduled onGameEnd events to handle FB's webhook reaching Replicant before this hook:
            await this.systemScheduledActions.unschedule({
                notificationId: ON_GAME_END_NOTIFICATION_ID,
                userData: { metainfoMVCC },
                userId: opts.id,
            });
        });

    handleCreateOrLogin: (opts: {
        clientAppName?: string;
        convertFromWebPlayer?: { token: string; userId: string };
        id: string;
        sessionId: string;
        sessionName?: string;
        onLoginActionArgs: any;
        ip?: string;
        skipOnLoginAction: boolean;
        prefetchKeys?: string[];
        prefetchInternalKeys?: string[];
    }) => Promise<CreateOrLoginOutput<T['state']>> = this.handler(async (opts) => {
        const {
            id,
            onLoginActionArgs,
            sessionId,
            sessionName,
            ip,
            prefetchKeys,
            prefetchInternalKeys,
            skipOnLoginAction,
        } = opts;

        const abTestsDynamicConfigPromise = this.abTests.getDynamicConfig().then((config) => config.abTests);

        // KV read errors are reported but do not block login:
        const prefetchKeysPromise = prefetchKeys?.length
            ? this.kvStore.getBatch(prefetchKeys).catch(async (error) => {
                  await this.errorReporter.captureException(error); // Report and swallow error
              })
            : Promise.resolve();
        const prefetchInternalKeysPromise = prefetchInternalKeys?.length
            ? this.internalKvStore.getBatch(prefetchInternalKeys).catch(async (error) => {
                  await this.errorReporter.captureException(error); // Report and swallow error
              })
            : Promise.resolve();

        const extraLoginDataPromise = this.getExtraLoginData(ip);

        logger.debug('REPLICANT: createOrLogin', id);
        const { entry, entryDiffs, messages } = await this.db.fetch(id, { consistentRead: false });

        const isNewUser = entry === null;
        const originalAbTestAssignments = entry?.metainfo.abTestAssignments ?? {};

        // If user has a payment subscription and >12 hours has elapsed since the last sync with Stripe, update local status:
        const paymentSubscriptionStatusPromise =
            entry && this.opts.stripe
                ? updatePaymentSubscriptionStatusIfExpired({
                      dateNow: this.dateNow,
                      db: this.db,
                      metainfoMVCC: entry.metainfoMVCC,
                      reportError: (error) => this.reportError(error),
                      stripe: this.opts.stripe,
                      userId: id,
                  })
                : undefined;

        try {
            let result = await this.createOrLoginHandler.createOrLogin({
                clientAppName: opts.clientAppName,
                convertFromWebPlayer: opts.convertFromWebPlayer,
                getAbTestAssignments: (opts) =>
                    this.abTests.getAssignments({
                        currentAssignments: opts.entry?.metainfo.abTestAssignments ?? {},
                        isNewUser: opts.isNewUser,
                        userId: opts.userId,
                        userState: opts.entry?.state,
                    }),
                id,
                sessionId,
                sessionName,
                entry,
            });

            const queue: ReplicationEntry[] = [];

            if (this.config.onLoginAction && !this.config.actions[this.config.onLoginAction]) {
                throw Error(`Invalid onLoginAction: '${this.config.onLoginAction}' not found in config.actions`);
            }

            const onLoginAction =
                !skipOnLoginAction && this.config.onLoginAction
                    ? this.config.actions[this.config.onLoginAction]
                    : undefined;

            // Reduce messages into state via an internal APPLY_MESSAGES_ACTION:
            if (messages.length > 0) {
                queue.push({
                    async: false,
                    fn: APPLY_MESSAGES_ACTION,
                    args: messages.map((msg) => msg.id),
                });
            }

            // Resolve onLoginAction if one is configured:
            if (onLoginAction) {
                queue.push({
                    async: onLoginAction.type === 'async',
                    fn: this.config.onLoginAction as string,
                    args: onLoginActionArgs,
                    isServerSide: true,
                });
            }

            // Resolve module onLoginActions:
            const modules: ReplicantModule[] = this.config.modules ?? [];

            for (const module of modules) {
                if (module.config.onLoginAction) {
                    const namespacedActionName = `${module.name}.${module.config.onLoginAction}`;

                    const moduleOnLoginAction = getNamespacedValue(
                        module.config.actions as Actions<any>,
                        namespacedActionName,
                    );

                    if (!moduleOnLoginAction) {
                        throw Error(
                            `Invalid onLoginAction in module '${module.name}': '${module.config.onLoginAction}' not found in config.actions.${module.name}`,
                        );
                    }

                    queue.push({
                        async: moduleOnLoginAction.type === 'async',
                        fn: namespacedActionName,
                        isServerSide: true,
                    });
                }
            }

            // Pass dynamic A/B tests config to the client:
            const abTestsDynamicConfig: {
                [testId: string]: { active: boolean; rollOut: number; stopAssignment?: boolean };
            } = {};

            for (const [testId, testConfig] of Object.entries(await abTestsDynamicConfigPromise)) {
                if (!testConfig) {
                    continue;
                }

                const { active, rollOut, newUsersRollOut, stopAssignment } = testConfig;

                abTestsDynamicConfig[testId] = {
                    active,
                    rollOut: isNewUser && newUsersRollOut !== undefined ? newUsersRollOut : rollOut,
                    stopAssignment,
                };
            }

            if (queue.length > 0) {
                const replicateResult = await this.replicateHandler.replicate({
                    abTestsDynamicConfig,
                    consistentFetchIds: [id], // In case freshly created own state is fetched in onLogin action
                    entry: result,
                    entryDiffs,
                    id,
                    messages,
                    queue,
                    sessionId,
                    sessionName,
                });

                result = replicateResult.entry;
            }

            const token = this.opts.encodeToken({
                playerId: id,
                appName: getAppName(this.config),
                dateNow: this.dateNow,
            });

            if (this.supportsIndexing) {
                const indexDoc = generateIndexDoc(id, result, this.config, this.dateNow);
                await this.pushDocsToIndex({ [id]: indexDoc });
            }

            const [extraData, paymentSubscriptionStatus, prefetchedKeys, prefetchedInternalKeys] = await Promise.all([
                extraLoginDataPromise,
                paymentSubscriptionStatusPromise,
                prefetchKeysPromise,
                prefetchInternalKeysPromise,
            ]);

            if (paymentSubscriptionStatus) {
                result.metainfoMVCC.paymentSubscription = paymentSubscriptionStatus;
            }

            const userAssetsBaseUrl = this.devOpts.userAssetsBaseUrl;
            const chatbotAssetUrls = this.devOpts.chatbotAssetUrls;

            const abTestChangeEvents = calculateAssignmentChanges({
                oldAssignments: originalAbTestAssignments,
                newAssignments: result.metainfo.abTestAssignments ?? {},
            });

            return {
                ...result,
                abTestsDynamicConfig,
                prefetchedKeys: prefetchedKeys || undefined,
                prefetchedInternalKeys: prefetchedInternalKeys || undefined,
                token,
                userAssetsBaseUrl,
                chatbotAssetUrls,
                extraData,
                abTestChangeEvents,
            };
        } catch (e: any) {
            e.stateContext = { id, entry, messages };
            throw e;
        }
    });

    handleAdminCreateUser: (opts: {
        id: string;
        args?: unknown;
    }) => Promise<{ data: boolean; t: number; service: number }> = this.handler(async (opts) => {
        const { id, args } = opts;

        logger.debug('REPLICANT: adminCreateMissingUser', id);
        const { entry, entryDiffs, messages } = await this.db.fetch(id, { consistentRead: false });

        const isNewUser = entry === null;

        if (!isNewUser) {
            return false;
        }

        try {
            let result = await this.createOrLoginHandler.createUser({
                getAbTestAssignments: (opts) =>
                    this.abTests.getAssignments({
                        currentAssignments: {},
                        isNewUser: true,
                        userId: id,
                        userState: undefined,
                    }),
                id,
            });

            const queue: ReplicationEntry[] = [];

            if (this.config.onAdminCreateUserAction && !this.config.actions[this.config.onAdminCreateUserAction]) {
                throw Error(
                    `Invalid onAdminCreateUserAction: '${this.config.onAdminCreateUserAction}' not found in config.actions`,
                );
            }

            const onAdminCreateUserAction = this.config.onAdminCreateUserAction
                ? this.config.actions[this.config.onAdminCreateUserAction]
                : undefined;

            // Resolve onLoginAction if one is configured:
            if (onAdminCreateUserAction) {
                queue.push({
                    async: onAdminCreateUserAction.type === 'async',
                    fn: this.config.onAdminCreateUserAction as string,
                    args,
                    isServerSide: true,
                });
            }

            if (queue.length > 0) {
                const replicateResult = await this.replicateHandler.replicate({
                    abTestsDynamicConfig: {},
                    consistentFetchIds: [id], // In case freshly created own state is fetched in onAdminCreateUser action
                    entry: result,
                    entryDiffs,
                    id,
                    messages,
                    queue,
                });

                result = replicateResult.entry;
            }

            if (this.supportsIndexing) {
                const indexDoc = generateIndexDoc(id, result, this.config, this.dateNow);
                await this.pushDocsToIndex({ [id]: indexDoc });
            }

            return true;
        } catch (e: any) {
            e.stateContext = { id, entry, messages };
            throw e;
        }
    });

    handleGetToken: (id: string) => Promise<GetTokenOutput> = this.handler(async (id) => {
        try {
            const token = this.opts.encodeToken({
                playerId: id,
                appName: getAppName(this.config),
                dateNow: this.dateNow,
            });
            return { token };
        } catch (e: any) {
            e.stateContext = { id };
            throw e;
        }
    });

    handleReplicate: (opts: {
        abTestsDynamicConfig: ABTestsDynamicConfig;
        id: string;
        queue: ReplicationEntry[];
        clientRandomSeed: number;
        rev: number;
        requestedProfileIds?: string[];
        sessionId: string;
        sessionName?: string;
        clientRequestId: string;
        consistentFetchIds: string[];
    }) => Promise<ReplicateOutput<T['state'], T['ruleset']>> = this.handler(async (opts) => {
        const stateContext: any = {};

        try {
            return await this.replicateHandler.handleReplicateRequest({
                ...opts,
                pushDocToIndex: (userId, doc) => this.pushDocsToIndex({ [userId]: doc }),
                stateContext,
            });
        } catch (e: any) {
            e.stateContext = stateContext;
            e.code = e.code || ReplicantErrorCode.replication_error;
            throw e;
        }
    });

    handleFetchStates: (
        ids: string[],
        friendRevs: { [id: string]: number },
        consistentFetchIds: string[],
    ) => Promise<FetchStatesOutput<T['state'], T['ruleset']>> = this.handler((ids, friendRevs, consistentFetchIds) =>
        this.fetchStatesHandler.fetchStates(ids, { friendRevs, consistentFetchIds }),
    );

    handleInferGenderFromName: (name: string) => Promise<InferGenderFromNameOutput> = this.handler(async (name) => {
        try {
            return await this.devOpts.inferGenderFromName(name);
        } catch (originalError: any) {
            const error = new ReplicantError(originalError.message, 'server_error', 'gender_inference_error');
            error.stack = originalError.stack;
            throw error;
        }
    });

    handleOtpInitiate: (opts: InitiateOtpOpts) => Promise<WithMetainfo<OTPRequestResponse>> = this.handler(
        async (opts) => this.otp.initiateOtp(opts),
    );

    handleSocialGraphUpdateSelf: (opts: {
        playerId: string;
        nonPlayerFriendIds: string[];
        meta: { [key: string]: unknown };
    }) => Promise<WithMetainfo<void>> = this.handler(async (opts) => {
        await this.socialGraph.updatePlayer(opts);
    });

    handleSocialGraphTrackInteractions: (opts: {
        senderId: string;
        interactions: SocialGraphInteraction[];
    }) => Promise<WithMetainfo<void>> = this.handler(async (opts) => {
        await this.socialGraph.trackInteractions(opts);
    });

    handleUserAssetUpload: (opts: { dataUrl: string; fileNamePrefix?: string }) => Promise<UserAssetUploadOutput> =
        this.handler(async (opts) => {
            const assetUrl = await this.assetUploader.uploadDataURLAsset(opts);

            return { assetUrl };
        });

    handleWriteKvPairs: (
        pairs: { [key: string]: string },
        opts?: KeyValueStoreSendOpts,
    ) => Promise<WriteKvPairsOutput> = this.handler(async (pairs, opts) => {
        await this.kvStore.sendBatch(pairs, opts);
        return true;
    });

    handleFetchKvPairs: (keys: string[]) => Promise<KvPairOutput> = this.handler(async (keys) => {
        return this.kvStore.getBatch(keys);
    });

    handleFetchInternalKvPairs: (keys: string[]) => Promise<KvPairOutput> = this.handler(async (keys) => {
        return this.internalKvStore.getBatch(keys);
    });

    handleNukeUser: (opts: {
        userId: string;
        sources?: {
            amplitude: boolean;
            sentry: boolean;
            replicant: boolean;
            indexDB: boolean;
        };
    }) => Promise<WithMetainfo<void>> = this.handler(async (opts) => {
        if (!opts.sources) {
            opts.sources = {
                amplitude: false,
                sentry: false,
                replicant: true,
                indexDB: true,
            };
        }

        const promises: Promise<any>[] = [];
        const errors: string[] = [];
        const dataSourceMap: string[] = [];

        if (opts.sources.amplitude) {
            // TODO in later PR
        }

        if (opts.sources.sentry) {
            // TODO in later PR
        }

        if (opts.sources.indexDB && this.supportsIndexing) {
            if (this.opts.indexDB) {
                dataSourceMap.push('indexDB');
                promises.push(this.opts.indexDB.delete(opts.userId));
            } else {
                errors.push('indexDB undefined');
            }
        }

        if (opts.sources.replicant) {
            dataSourceMap.push('replicant');
            promises.push(this.db.delete(opts.userId));
        }

        const results = await Promise.allSettled(promises);
        results.forEach((result, index: number) => {
            if (result.status === 'rejected') {
                errors.push(dataSourceMap[index] + ' - ' + result.reason);
            }
        });

        if (errors.length > 0) {
            throw new Error(errors.join('; '));
        }
    });

    handleNukeDB: () => Promise<WithMetainfo<void>> = this.handler(async () => {
        await this.db.nukeDB();
        if (this.supportsIndexing) {
            await this.opts.indexDB?.recreateIndex();
        }

        if (this.opts.sharedStateIndexDBs) {
            await Promise.all(Object.values(this.opts.sharedStateIndexDBs).map((indexDB) => indexDB.recreateIndex()));
        }
    });

    handleSetTestUsers: (ownerId: string, states: { [idSuffix: string]: T['state'] }) => Promise<SetTestUsersOutput> =
        this.handler(async (ownerId, states) => {
            const idMaps = await Promise.all(
                Object.entries(states).map(async ([testUserId, partialState]) => {
                    const id = ownerId + '_' + testUserId;

                    let state: T['state'] = deepMerge(this.config.stateSchema.getDefault()!, partialState);
                    try {
                        state = this.stateSchema.tryDecode(state);
                    } catch (e: any) {
                        e.stateContext = { id };
                        throw e;
                    }

                    const { entry, messages } = await this.db.fetch(id);

                    try {
                        const loggedInEntry = await this.createOrLoginHandler.createOrLogin({ id, entry });
                        const newEntry = { ...loggedInEntry, state };
                        await this.db.write(id, newEntry);

                        // Test user state was reset so delete any old messages:
                        if (messages.length > 0) {
                            await this.db.deleteMessages(id, messages);
                        }

                        // If indexing is enabled, add the test users to the index.
                        if (this.supportsIndexing) {
                            const indexDoc = generateIndexDoc(id, newEntry, this.config, this.dateNow);
                            await this.pushDocsToIndex({ [id]: indexDoc });
                        }
                    } catch (e: any) {
                        e.stateContext = { id, entry, messages };
                        throw e;
                    }

                    return { [testUserId]: id };
                }),
            );

            const testUsersMap = Object.assign({}, ...idMaps);
            return { testUsersMap };
        });

    handleAdminAction: (action: string, args: unknown) => Promise<WithMetainfo<any>> = this.handler(
        async (action, args) => this.adminHandler.handleAdminAction({ action, args }),
    );

    // iOS bridge facility.

    handleIOSBridgeFetch: (id: string, uuid: string) => Promise<WithMetainfo<string | null>> = this.handler(
        async (id, uuid) => {
            const key = 'ios_' + id + '_' + uuid;
            const value = await this.db.kvStore.fetch(key, this.dateNow());
            if (value) {
                await this.db.kvStore.delete(key);
            }
            return value;
        },
    );

    handleIOSBridgePut: (id: string, uuid: string, payload: string, timestamp: number) => Promise<WithMetainfo<void>> =
        this.handler(async (id, uuid, payload, timestamp) => {
            const key = 'ios_' + id + '_' + uuid;
            if (payload.length >= 100000) {
                throw new ReplicantError('iOS bridge payload too large', 'server_error', 'payload_too_large');
            }

            await this.db.kvStore.write(key, payload, timestamp);
        });

    // iOS APN

    handleIOSPostAPN: (opts: {
        receiverId: string;
        notification: APNProps;
        payload: AnalyticsPayload;
        imageUrl?: string;
        analyticsUserProps?: { [key: string]: unknown };
    }) => Promise<PushNotificationOutput> = this.handler(
        async ({ receiverId, notification, payload, imageUrl, analyticsUserProps }) => {
            const receiverChatbotMetainfo = await this.db.chatbot.fetchMetainfo(receiverId);

            return this.doIOSPostAPN({
                receiverId,
                receiverChatbotMetainfo: receiverChatbotMetainfo ?? {},
                notification,
                payload,
                imageUrl,
                analyticsUserProps,
            });
        },
    );

    // Android/FCM

    handleAndroidPushNotification: (opts: {
        receiverId: string;
        notification: FCMProps;
        payload: AnalyticsPayload;
        imageUrl?: string;
        analyticsUserProps?: { [key: string]: unknown };
    }) => Promise<PushNotificationOutput> = this.handler(
        async ({ receiverId, notification, payload, imageUrl, analyticsUserProps }) => {
            const receiverChatbotMetainfo = await this.db.chatbot.fetchMetainfo(receiverId);

            return this.doAndroidPushNotification({
                receiverId,
                receiverChatbotMetainfo: receiverChatbotMetainfo ?? {},
                notification,
                payload,
                imageUrl,
                analyticsUserProps,
            });
        },
    );

    handlePushNotification: (opts: {
        receiverId: string;
        notificationPlatformProps: PushNotificationPlatformProps;
        payload: AnalyticsPayload;
        imageUrl?: string;
        analyticsUserProps?: { [key: string]: unknown };
    }) => Promise<PushNotificationPlatformsOutput> = this.handler(
        async ({ receiverId, notificationPlatformProps, payload, imageUrl, analyticsUserProps }) => {
            const receiverChatbotMetainfo = await this.db.chatbot.fetchMetainfo(receiverId);

            const results = await Promise.all([
                this.doIOSPostAPN({
                    receiverId,
                    receiverChatbotMetainfo: receiverChatbotMetainfo ?? {},
                    notification: notificationPlatformProps.ios,
                    payload,
                    imageUrl,
                    analyticsUserProps,
                }),
                this.doAndroidPushNotification({
                    receiverId,
                    receiverChatbotMetainfo: receiverChatbotMetainfo ?? {},
                    notification: notificationPlatformProps.android,
                    payload,
                    imageUrl,
                    analyticsUserProps,
                }),
            ]);

            return {
                ios: results[0],
                android: results[1],
            };
        },
    );

    // Handlers.

    readonly abTests: ServerReplicantABTests;
    readonly airtable: ServerReplicantAirtable;
    readonly asyncGetters: ServerReplicantAsyncGetters<T>;
    readonly chatbot: ServerReplicantChatbot<T>;
    readonly exchangeRateHandler: ServerReplicantExchangeRate;
    readonly internalKvStore: KeyValueStoreAPI;
    readonly kvStore: KeyValueStoreAPI;
    readonly loginTokens: ServerReplicantLoginTokens;
    readonly otp: ServerReplicantOTP;
    /** Internal KV store is read-only to clients. */
    readonly scheduledActions: ServerReplicantScheduledActions<T>;
    readonly sharedStates: ServerReplicantSharedStates;
    readonly socialGraph: ServerReplicantSocialGraph;
    readonly systemScheduledActions: ServerReplicantSystemScheduledActions;

    private ecpm: ServerReplicantECPM;
    private iosNotifications: ServerReplicantIOSPushNotifications<T>;
    private androidNotifications: ServerReplicantAndroidPushNotifications;
    private analytics: ServerReplicantAnalytics<T>;
    private oauth: ServerReplicantOAuth;
    private lineMessageClient?: LineMessageClient;
    private userIdEncryption: ServerReplicantUserIDEncryption;

    private createOrLoginHandler: ServerReplicantCreateOrLogin<T>;
    private fetchStatesHandler: ServerReplicantFetchStates<T>;
    private replicateHandler: ServerReplicantReplicate<T>;

    private adminHandler: ServerReplicantAdmin<T>;
    private atoneWebhookHandler?: AtoneWebhookHandler;
    private fbWebhookHandler: ReturnType<typeof createFBWebhookHandler>;
    private instagramWebhookHandler: InstagramWebhookHandler<T>;
    private messengerWebhookHandler: MessengerWebhookHandler<T>;
    private lineWebhookHandler: LineWebhookHandler<T>;
    private komojuWebhookHandler?: KomojuWebhookHandler;
    private xsollaWebhookHandler?: XsollaWebhookHandler;
    private stripeWebhookHandler?: StripeWebhookHandler;
    private telegramWebhookHandler?: TelegramWebhookHandler;
    private zoomWebhookHandler: ZoomWebhookHandler<T>;

    //

    private messages: T['messages'];
    private stateSchema: SB.Schema<T['state']>;
    private devOpts: ServerReplicantDevOptions;

    // used to track when flushing the changeset to db completes. Used in tests to confirm changesets are written
    private changesetsFlushedPromise: Promise<void> = Promise.resolve();

    private db: DB<T['state'], T['messages']>;
    assetUploader: ServerAssetUploader;

    private supportsIndexing: boolean;

    constructor(
        readonly config: ReplicantConfig<T>,

        private opts: {
            aiClient: AIClient;
            airtable: AirtableClient;
            assetUploader: ServerAssetUploader;
            atoneClient?: AtoneClient;
            createActionAPI: (opts: CreateServerReplicantActionAPIOpts<T>) => ReplicantAsyncActionAPI<T>;
            crypto: CryptoUtils;
            discordClient?: DiscordClient;
            lineOAuthClient?: LineOAuthClient;
            db: DB<T['state'], T['messages']>;
            encodeToken: (opts: {
                playerId: string;
                appName: string;
                expiresInMs?: number;
                dateNow: () => number;
            }) => string;
            fetcher: Fetcher;
            generateWebPlayerAuthToken: (playerId: string) => string;
            generateWebPlayerId: () => string;
            gzipToBase64: (input: string) => string;
            indexDB?: IndexDB<any>;
            indexingQueue?: IndexingQueue;
            komojuClient?: KomojuClient;
            xsollaClient?: XsollaClient;
            narrowcastAPIClient: NarrowcastAPIClient;
            options?: Partial<ServerReplicantDevOptions>;
            platform: ReplicantPlatform;
            scheduledActionsQueue: ScheduledActionsQueue;
            sharedStateIndexDBs?: SharedStateIndexDBs<any>;
            stripe?: StripeClient;
            taskRunnerClient?: TaskRunnerClient;
            telegramClient?: TelegramClient;
            twilio: TwilioClient;
            webPush: WebPushClient;
            lineMessageClient?: LineMessageClient;
            instagramClient?: InstagramClient;
        },
    ) {
        const { options, platform, db, indexDB, assetUploader } = opts;

        this.devOpts = {
            simulatedLatency: 0,
            dateNow: Date.now,
            lookupGeoIp: () => undefined,
            postFBMessage: () => Promise.resolve({ body: '', statusCode: 200 }),
            postLineMessage: () => Promise.resolve({ body: '', statusCode: 200 }),
            postZoomMessage: () => Promise.resolve({ body: '', statusCode: 200 }),
            postAnalytics: () => Promise.resolve(),
            postActivePushNotification: () => Promise.resolve({ success: true }),
            postFirebaseCloudMessage: () => Promise.resolve({ success: true }),
            postNativeBridgeSecret: () => Promise.resolve(),
            reduceMessagesMinThreshold: 100,
            reduceMessagesMinInactiveMs: duration({ minutes: 1 }),
            chatbotAssetUrls: {},
            userAssetsBaseUrl: '',
            disableExchangeRatesLocalCache: false,
            abTestsCacheExpiresInMs: duration({ minutes: 1 }),
            sendTemplatedEmail:
                options?.sendTemplatedEmail ??
                (async () => {
                    throw new Error('sendTemplatedEmail not defined');
                }),
            ...options,
            inferGenderFromName: options?.inferGenderFromName ?? (async () => 'unknown'),
            validateChatbotSubscription: options?.validateChatbotSubscription ?? defaultValidateChatbotSubscription,
        };

        this.db = db;
        this.assetUploader = assetUploader;

        validateConfig(config);

        this.kvStore = new ServerReplicantKeyValueStore({
            dateNow: this.dateNow,
            db,
        });
        this.internalKvStore = new ServerReplicantInternalKeyValueStore({
            dateNow: this.dateNow,
            db,
        });

        this.loginTokens = new ServerReplicantLoginTokens({
            dateNow: this.dateNow,
            db,
            createWebPlayerToken: (opts) => this.opts.generateWebPlayerAuthToken(opts.userId),
        });

        this.sharedStates = new ServerReplicantSharedStates({
            config,
            db,
            dateNow: this.dateNow,
            errorReporter: this.errorReporter,
            isInitialDevelopment: () => this.isInitialDevelopment(),
            pushSharedStateDocToIndex: (...args) => this.pushSharedStateDocToIndex(...args),
        });

        this.messages = config.messages ?? ({} as any);
        this.stateSchema = config.stateSchema;

        this.supportsIndexing =
            !!this.opts.indexDB &&
            !!this.config.computedProperties &&
            Object.values(this.config.computedProperties).some((x) => !!x._searchable);

        this.airtable = new ServerReplicantAirtable({
            airtable: opts.airtable,
            dateNow: this.dateNow,
            db,
            errorReporter: this.devOpts.errorReporter,
        });

        this.asyncGetters = new ServerReplicantAsyncGetters({
            aiClient: opts.aiClient,
            apiAccess: this,
            atoneClient: this.opts.atoneClient,
            db,
            komojuClient: this.opts.komojuClient,
            telegramClient: opts.telegramClient,
            xsollaClient: this.opts.xsollaClient,
            stripe: opts.stripe,
            validateChatbotSubscription: this.devOpts.validateChatbotSubscription,
            fetcher: opts.fetcher,
            sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
            userAssetsBaseUrl: this.devOpts.userAssetsBaseUrl,
        });

        this.ecpm = new ServerReplicantECPM({
            db,
            dateNow: this.dateNow,
        });

        this.iosNotifications = new ServerReplicantIOSPushNotifications({
            db,
            postActivePushNotification: this.devOpts.postActivePushNotification,
            sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
            errorReporter: this.devOpts.errorReporter,
            dateNow: this.dateNow,
        });

        this.androidNotifications = new ServerReplicantAndroidPushNotifications({
            db,
            postFirebaseCloudMessage: this.devOpts.postFirebaseCloudMessage,
            sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
            errorReporter: this.errorReporter,
            dateNow: this.dateNow,
        });

        this.analytics = new ServerReplicantAnalytics({
            config,
            dateNow: this.dateNow,
            errorReporter: this.errorReporter,
            getReadOnlyEntry: (...args) => this.getReadOnlyEntry(...args),
            postAnalytics: (events) => this.devOpts.postAnalytics(events),
        });

        this.oauth = new ServerReplicantOAuth({
            platform,
            discordClient: this.opts.discordClient,
            lineOAuthClient: this.opts.lineOAuthClient,
        });

        this.systemScheduledActions = new ServerReplicantSystemScheduledActions({
            config,
            dateNow: this.dateNow,
            db,
            errorReporter: this.errorReporter,
            resolveDelayedActions: (...args) => this.resolveDelayedActions(...args),
        });

        this.lineMessageClient = this.opts.lineMessageClient;

        this.userIdEncryption = new ServerReplicantUserIDEncryption({ crypto: this.opts.crypto });

        const chatbotOpts: ServerReplicantChatbotOptions<T> = {
            apiAccess: this,
            createPayloadEncoderAPI: (...args) => this.createPayloadEncoderAPI(...args),
            db,
            instagramClient: this.opts.instagramClient,
            postFBMessage: (...args) => this.devOpts.postFBMessage(...args),
            postLineMessage: (...args) => this.devOpts.postLineMessage(...args),
            postZoomMessage: (...args) => this.devOpts.postZoomMessage(...args),
            sendAndroidPushNotification: (...args) => this.androidNotifications.sendAndroidPushNotification(...args),
            sendIOSPushNotification: (...args) => this.iosNotifications.sendIOSPushNotification(...args),
            telegramClient: opts.telegramClient,
            twilioClient: opts.twilio,
            webPushClient: opts.webPush,
        };

        this.chatbot = new ServerReplicantChatbot(chatbotOpts);

        this.scheduledActions = new ServerReplicantScheduledActions({
            config,
            dateNow: this.dateNow,
            db,
            errorReporter: this.errorReporter,
            createEventHandlerApi: (...args) => this.createEventHandlerApi(...args),
            getReadOnlyEntry: (...args) => this.getReadOnlyEntry(...args),
            narrowcastAPIClient: this.opts.narrowcastAPIClient,
            resolveDelayedActions: (...args) => this.resolveDelayedActions(...args),
            pushDocsToIndex: (...args) => this.pushDocsToIndex(...args),
            queue: this.opts.scheduledActionsQueue,
        });

        this.createOrLoginHandler = new ServerReplicantCreateOrLogin({
            apiAccess: this,
            db,
            writeChangeset: (...args) => this.writeChangeset(...args),
            migrateUserSharedStates: (...args) => this.sharedStates.migrateUserSharedStates(...args),
        });

        this.fetchStatesHandler = new ServerReplicantFetchStates({
            apiAccess: this,
            gzipToBase64: this.opts.gzipToBase64,
            metrics: this.devOpts.metrics,
        });

        this.replicateHandler = new ServerReplicantReplicate({
            apiAccess: this,
            applyMessagesToState: (...args) => this.applyMessagesToState(...args),
            config,
            createActionAPI: this.createActionAPI,
            db,
            errorReporter: this.errorReporter,
            getApplicableMessages: (...args) => getApplicableMessages(this.messages, ...args),
            writeChangeset: (...args) => this.writeChangeset(...args),
            fetcher: this.opts.fetcher,
        });

        this.adminHandler = new ServerReplicantAdmin({
            apiAccess: this,
            chatbot: new ServerReplicantChatbot(chatbotOpts),
            db,
            fetcher: this.opts.fetcher,
            indexDB,
            narrowcastAPIClient: this.opts.narrowcastAPIClient,
            postMessages: (opts) =>
                this.postMessages(opts).then(async (indexDocs) => {
                    await this.pushDocsToIndex(indexDocs);
                    return indexDocs;
                }),
            deleteUserData: async (...args) => {
                await this.handleNukeUser(...args);
            },
            taskRunnerClient: this.opts.taskRunnerClient,
        });

        this.fbWebhookHandler = createFBWebhookHandler({
            chatbot: this.chatbot,
            config,
            createEventHandlerApi: (...args) => this.createEventHandlerApi(...args),
            dateNow: this.dateNow,
            db,
            errorReporter: this.devOpts.errorReporter,
            fetchNativeBridgeSecret: (...args) => this.fetchNativeBridgeSecret(...args),
            getReadOnlyEntry: (...args) => this.getReadOnlyEntry(...args),
            messengerIdToPlayerId: (...args) => this.messengerIdToPlayerId(...args),
            postNativeBridgeSecret: (...args) => this.devOpts.postNativeBridgeSecret(...args),
            pushDocsToIndex: (...args) => this.pushDocsToIndex(...args),
            resolveDelayedActions: (delayedActions) => this.resolveDelayedActions(delayedActions),
            sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
            systemScheduledActions: this.systemScheduledActions,
        });

        this.lineWebhookHandler = new LineWebhookHandler({
            apiAccess: this,
            config,
            db,
            errorReporter: this.errorReporter,
            pushDocsToIndex: (...args) => this.pushDocsToIndex(...args),
            sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
            dateNow: this.dateNow,
        });

        this.zoomWebhookHandler = new ZoomWebhookHandler({
            apiAccess: this,
            db,
            pushDocsToIndex: (...args) => this.pushDocsToIndex(...args),
            sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
        });

        this.instagramWebhookHandler = new InstagramWebhookHandler<T>({
            apiAccess: this,
            db,
            errorReporter: this.errorReporter,
            pushDocsToIndex: (...args) => this.pushDocsToIndex(...args),
            sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
        });

        this.messengerWebhookHandler = new MessengerWebhookHandler<T>({
            apiAccess: this,
            db,
            errorReporter: this.errorReporter,
            pushDocsToIndex: (...args) => this.pushDocsToIndex(...args),
            sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
        });

        this.otp = new ServerReplicantOTP({
            config,
            crypto: this.opts.crypto,
            dateNow: this.dateNow,
            db,
            errorReporter: this.devOpts.errorReporter,
            sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
            twilio: opts.twilio,
        });

        if (opts.options?.createAtoneWebhookHandler) {
            this.atoneWebhookHandler = opts.options.createAtoneWebhookHandler({
                sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
            });
        }

        if (opts.options?.createKomojuWebhookHandler && opts.komojuClient) {
            this.komojuWebhookHandler = opts.options.createKomojuWebhookHandler({
                sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
            });
        }

        if (opts.options?.createXsollaWebhookHandler && opts.xsollaClient) {
            this.xsollaWebhookHandler = opts.options.createXsollaWebhookHandler({
                sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
            });
        }

        if (opts.options?.createStripeWebhookHandler) {
            this.stripeWebhookHandler = opts.options?.createStripeWebhookHandler({
                db,
                decryptUserId:
                    this.platform === 'line'
                        ? (encryptedUserId) => this.userIdEncryption.decryptUserId(encryptedUserId)
                        : undefined,
                sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
                dateNow: this.dateNow,
            });
        }

        if (this.opts.telegramClient) {
            this.telegramWebhookHandler = createTelegramWebhookHandler({
                apiAccess: this,
                db,
                errorReporter: this.errorReporter,
                pushDocsToIndex: (...args) => this.pushDocsToIndex(...args),
                sendAnalyticsEvents: (...args) => this.sendAnalyticsEvents(...args),
                telegramClient: this.opts.telegramClient,
            });
        }

        this.socialGraph = new ServerReplicantSocialGraph(db);
        this.exchangeRateHandler = new ServerReplicantExchangeRate({
            db,
            devOpts: this.devOpts,
            fetcher: this.opts.fetcher,
        });

        this.abTests = new ServerReplicantABTests({ db, config, devOpts: this.devOpts });
    }

    get changesetsFlushed() {
        return this.changesetsFlushedPromise;
    }

    get errorReporter(): ErrorReporter {
        return this.devOpts.errorReporter ?? mockErrorReporter;
    }

    get platform(): ReplicantPlatform {
        return this.opts.platform;
    }

    get version() {
        return this.config.version;
    }

    dateNow = (): number => this.devOpts.dateNow();

    async handleCreateOrLoginWebPlayer(opts: {
        clientAppName?: string;
        id?: string;
        loginToken?: { token: string; userId: string };
        sessionId: string;
        sessionName?: string;
        onLoginActionArgs: any;
        otp?: OTPVerificationOpts;
        ip?: string;
        skipOnLoginAction: boolean;
        prefetchKeys?: string[];
        prefetchInternalKeys?: string[];
    }): Promise<CreateOrLoginWebPlayerOutput<T['state']>> {
        let id = opts.id;

        if (!id) {
            if (opts.otp) {
                id = await this.otp.verifyOtpAndGetLinkedUserId(opts.otp);
            } else if (opts.loginToken) {
                const { token, userId } = opts.loginToken;
                const loginTokenIsValid = await this.loginTokens.consumeLoginToken({ token, userId });

                if (loginTokenIsValid) {
                    id = userId;
                } else {
                    throw Error('Invalid login token');
                }
            } else {
                // Generate an ID in case of a new anonymous web player:
                id = this.opts.generateWebPlayerId();
            }
        }

        const { data, ...rest } = await this.handleCreateOrLogin({ ...opts, id });

        if (opts.otp && !data.metainfo.hasPhoneNumber) {
            // User has added and verified a phone number but `APIMetainfo.hasPhoneNumber` is falsey.
            // This is possible if `api.otp.verifyOtp` resolves successfully but the same request fails before user entry is written to DB.
            // TODO: self-heal by writing `metainfo.hasPhoneNumber` to DB here.
            await this.errorReporter.captureException(
                Error('User with verified phone number has no APIMetainfo.hasPhoneNumber'),
                { user: { id, ipAddress: opts.ip || '' } },
            );
        }

        // Pass auth token to client which persists token locally for subsequent logins:
        const webPlayerAuthToken = this.opts.generateWebPlayerAuthToken(id);

        return {
            ...rest,
            data: { ...data, id, webPlayerAuthToken },
        };
    }

    handleOAuthGetAccessToken(opts: { clientId?: string; code: string; redirectUri?: string }): Promise<OAuthResponse> {
        return this.oauth.fetchAccessToken(opts);
    }

    sendAnalyticsEvents = async (events: AnalyticsEvent[], context?: ExecutionContext) => {
        await this.analytics.sendAnalyticsEvents(events, context);
    };

    async messengerIdToPlayerId(messengerId: string): Promise<string | null> {
        return this.db.internalKvStore.fetch(KV_MESSENGER_ID_PREFIX + messengerId);
    }

    async reportError(e: Error, extraContext?: { [key: string]: any }) {
        return this.errorReporter.captureException(e, undefined, extraContext);
    }

    handleFBWebhook: (webhookEvents: FBWebhookEvent[]) => Promise<void> = async (webhookEvents) => {
        await this.fbWebhookHandler(webhookEvents);
    };

    handleInstagramWebhook: (requestBody: unknown) => Promise<void> = async (requestBody) => {
        await this.instagramWebhookHandler.handleWebhook(requestBody);
    };

    handleMessengerWebhook: (requestBody: unknown) => Promise<void> = async (requestBody) => {
        await this.messengerWebhookHandler.handleWebhook(requestBody);
    };

    handleAtoneWebhook: (eventRequest: { body: string; signature: string }) => Promise<void> = async (eventRequest) => {
        if (!this.atoneWebhookHandler) {
            throw Error(
                'Atone webhook handler is not configured. Make sure to define the ATONE_* environment variables.',
            );
        }

        await this.atoneWebhookHandler.handleEvent(eventRequest);
    };

    handleKomojuWebhook: (eventRequest: { body: string; signature: string }) => Promise<void> = async (
        eventRequest,
    ) => {
        if (!this.komojuWebhookHandler) {
            throw Error(
                'Komoju webhook handler is not configured. Make sure to define the KOMOJU_* environment variables.',
            );
        }

        await this.komojuWebhookHandler.handleEvent(eventRequest);
    };

    handleXsollaWebhook: (eventRequest: { body: string; signature: string }) => Promise<void> = async (
        eventRequest,
    ) => {
        if (!this.xsollaWebhookHandler) {
            throw Error(
                'Xsolla webhook handler is not configured. Make sure to define the XSOLLA_* environment variables.',
            );
        }

        await this.xsollaWebhookHandler.handleEvent(eventRequest);
    };

    handleLineWebhook: (webhookEvents: LineWebhookEvent[]) => Promise<void> = async (webhookEvents) => {
        await this.lineWebhookHandler.handleEvents(webhookEvents);
    };

    handleStripeWebhook: (webhookEvents: Stripe.Event) => Promise<void> = async (event) => {
        await this.stripeWebhookHandler?.(event);
    };

    handleTelegramWebhook: (eventRequest: { body: unknown; secretToken: string }) => Promise<void> = async (
        eventRequest,
    ) => {
        if (!this.telegramWebhookHandler) {
            throw Error(
                'Telegram webhook handler is not defined. Configure the TELEGRAM_BOT_TOKEN environment variable to enable Telegram webhooks.',
            );
        }

        await this.telegramWebhookHandler.handleEvent(eventRequest);
    };

    handleZoomWebhook: (webhookEvent: ZoomWebhookEvent) => Promise<void> = async (webhookEvent) => {
        await this.zoomWebhookHandler.handleEvent(webhookEvent);
    };

    async fetchNativeBridgeSecret(userId: string): Promise<string> {
        const metainfo = await this.db.chatbot.fetchMetainfo(userId);

        if (metainfo?.nativeBridgeSecret) {
            return metainfo.nativeBridgeSecret;
        }

        const bridgeSecret = generateRandomId();

        await this.setNativeBridgeSecret(userId, bridgeSecret);

        return bridgeSecret;
    }

    async setNativeBridgeSecret(userId: string, bridgeSecret: string) {
        await this.db.chatbot.setNativeBridgeSecret(userId, bridgeSecret);
    }

    async writeMessages(receiverId: string, messages: MessageItem[]): Promise<void> {
        if (messages.length === 0) {
            return;
        }

        // Report Sentry warnings on oversized arguments:
        await warnOnOversizeMessages(messages, this.errorReporter, { receiverId });

        await this.db.writeMessages(receiverId, messages);
    }

    postMessages: PostMessagesFn = async (opts) => {
        const userId = opts.receiverId;

        const { entryPromises } = opts.context ?? { entryPromises: {} };

        if (!entryPromises[userId]) {
            entryPromises[userId] = this.getReadOnlyEntry(userId, { reduceMessages: true }).then(
                (result) => (result?.entry as Entry<WithMeta<any, any, any>> | undefined) ?? null,
            );
        }

        const entry = await entryPromises[userId];

        const now = this.dateNow();
        const messageItems = opts.messages.map((msg) => ({ ...msg, timestamp: now + msg.senderClockOffset }));

        if (!entry) {
            if (this.config.throwOnMessageReceiverNotFound === false) {
                return {};
            }

            const receiverNotFoundError = Error('Trying to post a message to a user that does not exist') as Error & {
                userId: string;
                messages: MessageItem[];
            };

            receiverNotFoundError.messages = messageItems;
            receiverNotFoundError.userId = userId;

            throw receiverNotFoundError;
        }

        await this.writeMessages(
            userId,
            messageItems.filter((msg) => !msg.skipDbWrite),
        );

        if (this.supportsIndexing) {
            const virtual = createVirtualEntry(this.config, entry);

            const forceIndexing = opts.messages.some((msg) => msg.skipDbWrite); // Always index after message flush

            const newIndexDoc = await withIndexDocGeneration(
                userId,
                virtual.entry,
                this.config,
                this.dateNow,
                forceIndexing,
                () => this.migrateAndApplyMessages(userId, virtual, messageItems),
            );

            if (newIndexDoc) {
                return { [userId]: newIndexDoc };
            }
        }

        return {};
    };

    resolveDelayedActions: ResolveDelayedActionsFn = async (delayedActions, context) => {
        if (delayedActions.length === 0) {
            return;
        }

        const batchPromises: Promise<unknown>[] = [];
        const batchedMessageActions: DelayedAction[] = [];
        const analyticsEvents: AnalyticsEvent[] = [];
        const scheduledActionUpdates: (DelayedAction & { type: 'scheduleAction' | 'unscheduleAction' })[] = [];
        const sharedStateUpdates: SharedStateDelayedAction[] = [];

        const performanceContext = context?.performanceTracking?.context;

        for (const action of delayedActions) {
            if (action.type === 'action') {
                const wrappedAction = performanceContext
                    ? perf.withPerformanceTracking(action.action, performanceContext, action.apiMethodName)
                    : action.action;

                batchPromises.push(wrappedAction());
            } else if (
                action.type === 'createSharedState' ||
                action.type === 'deleteUserSharedState' ||
                action.type === 'postSharedStateMessage' ||
                action.type === 'setUserSharedState' ||
                action.type === 'increaseSharedStateCounter' ||
                action.type === 'setSharedStateCounter'
            ) {
                sharedStateUpdates.push(action);
            } else if (action.type === 'sendAnalyticsEvents') {
                // Batch all the api.sendAnalyticsEvents calls among delayed actions into a single events post:
                analyticsEvents.push(...action.events);
            } else if (action.type === 'postMessage') {
                // Combine messages with the same receiver to a single postMessages call:
                const sameReceiverAction = batchedMessageActions.find(
                    (a) => a.type === action.type && a.receiverId === action.receiverId,
                ) as typeof action | undefined;

                if (sameReceiverAction) {
                    sameReceiverAction.messages.push(...action.messages);
                } else {
                    batchedMessageActions.push(action);
                }
            }

            // Combine scheduled action updates into a single operation:
            else if (action.type === 'scheduleAction' || action.type === 'unscheduleAction') {
                scheduledActionUpdates.push(action);
            } else {
                unreachable(action);
            }
        }

        // Wrap delayed action resolves with performance tracking if the current request is sampled:
        const resolvers = {
            postMessages: this.postMessages.bind(this),
            resolveActionScheduleUpdates,
            resolveSharedStatesUpdates: this.sharedStates.resolveAllSharedStatesUpdates.bind(this.sharedStates),
            sendAnalyticsEvents: this.sendAnalyticsEvents.bind(this),
        };

        function wrapResolver<T extends keyof typeof resolvers>(property: T, apiMethodName: string): void {
            resolvers[property] = perf.withPerformanceTracking(resolvers[property], performanceContext!, apiMethodName);
        }

        if (performanceContext) {
            wrapResolver('postMessages', 'api.postMessage.*');
            wrapResolver('resolveActionScheduleUpdates', 'api.scheduledActions.*');
            wrapResolver('resolveSharedStatesUpdates', 'api.sharedStates.*');
            wrapResolver('sendAnalyticsEvents', 'api.sendAnalyticsEvents');
        }

        if (analyticsEvents.length > 0) {
            batchPromises.push(resolvers.sendAnalyticsEvents(analyticsEvents, context));
        }

        if (scheduledActionUpdates.length > 0) {
            batchPromises.push(
                resolvers.resolveActionScheduleUpdates({
                    db: this.db,
                    errorReporter: this.errorReporter,
                    now: this.dateNow(),
                    scheduleUpdates: scheduledActionUpdates,
                }),
            );
        }

        if (sharedStateUpdates.length > 0) {
            batchPromises.push(
                resolvers
                    .resolveSharedStatesUpdates(sharedStateUpdates)
                    .then(async (newIndexDocs: SharedStateIndexDocs) => {
                        const indexPromises: Promise<void>[] = [];

                        // TODO optimize by batching index doc queue message sends:
                        for (const stateName in newIndexDocs) {
                            for (const stateId in newIndexDocs[stateName]) {
                                const indexDoc = newIndexDocs[stateName]![stateId]!;

                                indexPromises.push(this.pushSharedStateDocToIndex({ stateName, stateId, indexDoc }));
                            }
                        }

                        await Promise.all(indexPromises);
                    }),
            );
        }

        if (this.lineMessageClient) {
            batchPromises.push(this.lineMessageClient.flush());
        }

        const postMessagesPromise = Promise.all(
            batchedMessageActions.map(async (action) => {
                if (action.type === 'postMessage') {
                    return resolvers.postMessages({
                        receiverId: action.receiverId,
                        messages: action.messages,
                        context,
                    });
                } else {
                    throw Error('batchedMessageActions should not contain other than postMessage actions');
                }
            }),
        ).then((indexDocs) => this.pushDocsToIndex(Object.assign({}, ...indexDocs)));

        batchPromises.push(postMessagesPromise);

        const errors: Error[] = [];
        let isFatal = false;

        await Promise.all(
            batchPromises.map((promise) =>
                promise.catch((err) => {
                    // Catch all errors, allowing other actions to complete
                    errors.push(err);
                    isFatal = isFatal || err.severity === 'fatal';
                }),
            ),
        );

        if (errors.length > 0) {
            const clientAggregateErrors = errors.map((e) =>
                e instanceof ReplicantError
                    ? { code: e.code, subCode: e.subCode }
                    : { code: ReplicantErrorCode.unknown_error },
            );

            throw new ReplicantError(
                'Error resolving delayed actions: ' + errors.map((e) => e.message).join('; '),
                'replication_error',
                'delayed_actions_error',
                isFatal ? 'fatal' : 'error',
                { errors }, // Report complete errors to Sentry
                { errors: clientAggregateErrors }, // Return error codes to the client
            );
        }
    };

    async fetchStates(
        ids: string[],
        opts: {
            friendRevs?: { [id: string]: number };
            consistentFetchIds: string[];
            disablePagination?: boolean;
        },
    ) {
        return this.fetchStatesHandler.fetchStates(ids, opts);
    }

    searchPlayers: IndexSearchFn<any> = async (query) => {
        if (this.opts.indexDB) {
            const results = this.opts.indexDB.query(query);
            return results;
        } else {
            const errorMessage =
                'IndexDB is undefined: searchPlayers will return empty result set. Make sure to configure Elasticsearch.';
            const error = new Error(errorMessage);
            await this.errorReporter.captureException(error, undefined, { query }); // Report and swallow error
            return { results: [] };
        }
    };

    countPlayers: IndexCountFn<any> = async (query) => {
        if (this.opts.indexDB) {
            return this.opts.indexDB.count(query);
        } else {
            const errorMessage =
                'IndexDB is undefined: countPlayers will return 0. Make sure to configure Elasticsearch.';
            const error = new Error(errorMessage);
            await this.errorReporter.captureException(error, undefined, { query }); // Report and swallow error
            return 0;
        }
    };

    searchSharedStates: SearchSharedStatesFn = async (sharedStateName, query) => {
        const sharedState = this.config.sharedStates?.[sharedStateName];

        if (!sharedState) {
            throw Error(`Shared state with name ${sharedStateName} is not found in configuration`);
        }

        if (!sharedStateHasSearchableProperties(sharedState)) {
            throw Error(`Shared state with name ${sharedStateName} does not have any searchable computed properties`);
        }

        const indexDB = this.opts.sharedStateIndexDBs?.[sharedStateName];

        // Report error and return an empty result in case Elasticsearch is not configured:
        if (!indexDB) {
            const error = new Error(
                'IndexDB is undefined: searchSharedStates will return an empty result set. Make sure to configure Elasticsearch.',
            );
            await this.errorReporter.captureException(error, undefined, { query });
            return { results: [] };
        }

        return indexDB.query(query);
    };

    countSharedStates: CountSharedStatesFn = async (sharedStateName, query) => {
        const sharedState = this.config.sharedStates?.[sharedStateName];

        if (!sharedState) {
            throw Error(`Shared state with name ${sharedStateName} is not found in configuration`);
        }

        if (!sharedStateHasSearchableProperties(sharedState)) {
            throw Error(`Shared state with name ${sharedStateName} does not have any searchable computed properties`);
        }

        const indexDB = this.opts.sharedStateIndexDBs?.[sharedStateName];

        // Report error and return an empty result in case Elasticsearch is not configured:
        if (!indexDB) {
            const error = new Error(
                'IndexDB is undefined: countSharedStates will return an empty result set. Make sure to configure Elasticsearch.',
            );
            await this.errorReporter.captureException(error, undefined, { query });
            return 0;
        }

        return indexDB.count(query);
    };

    async performHealthCheck(): Promise<void> {
        // Try to write and read back from the db.
        await this.db.write('impossible_id', {
            rev: 0,
            state: {
                example: 'yes',
            } as any,
            metainfo: { random: { n: 0 } },
            metainfoMVCC: {},
            version: 0,
            lastUpdated: Date.now(),
            createdAt: Date.now(),
            userSharedStates: {},
        });

        const { entry: readback } = await this.db.fetch('impossible_id');
        if (!readback || (readback.state as any).example !== 'yes') {
            throw new Error('Did not readback the correct statement.');
        }

        // Try to query indexing DB if enabled:
        if (this.opts.indexDB) {
            await this.opts.indexDB.query({
                limit: 1,
                where: { id: { isOneOf: ['impossible_id'] } } as SearchOpts<any>['where'],
            });
        }
    }

    /** @returns A user entry with migrations, messages and system state fields applied to it. */
    getReadOnlyEntry: GetReadOnlyEntryFn<T> = async (userId, opts) => {
        let isNewUser = false;

        const stateAndMessages = await this.db.fetch(userId, {
            consistentRead: opts?.consistentRead || false,
            skipWrites: opts?.skipWrites,
        });

        let { entry, messages } = stateAndMessages;

        if (!entry) {
            if (!opts?.createIfNotFound) {
                return null;
            }

            entry = await this.createOrLoginHandler.createOrLogin({
                id: userId,
                entry: null,
                getAbTestAssignments: opts?.assignABTests
                    ? (assignOpts) =>
                          this.abTests.getAssignments({
                              currentAssignments: assignOpts.entry?.metainfo.abTestAssignments ?? {},
                              isNewUser: assignOpts.isNewUser,
                              userId: assignOpts.userId,
                              userState: assignOpts.entry?.state,
                          })
                    : undefined,
            });
            isNewUser = true;
            messages = [];
        }

        const shouldAssignABTests = !isNewUser && opts?.assignABTests;

        const abTestAssignments = shouldAssignABTests
            ? await this.abTests.getAssignments({
                  userId,
                  isNewUser: false,
                  currentAssignments: entry.metainfo.abTestAssignments ?? {},
                  userState: entry.state,
              })
            : undefined;

        const previouslyReducedMessages = messages.filter((msg) => isMessageRecentlyReduced(entry?.metainfo, msg));

        const oldAbTestsState = JSON.stringify(entry.metainfo.abTestAssignments);
        const { virtual, reducedMessages, unreducedMessages } = this.migrateAndApplyMessages(userId, entry, messages, {
            abTestAssignments,
        });

        entry = virtual.entry;
        const newAbTestsState = JSON.stringify(entry.metainfo.abTestAssignments);

        // A newer backend version may have migrated user to a version higher than this backend version's latest migration: in this case skip entry write to avoid overwriting the migration.
        const isValidSchemaVersion = this.config.migrator.getLatestVersion() === entry.version;

        const shouldWriteABTestAssignments = shouldAssignABTests && newAbTestsState !== oldAbTestsState;

        const userIsInactive = this.dateNow() - entry.lastUpdated >= this.devOpts.reduceMessagesMinInactiveMs;
        const shouldWriteReducedMessages =
            opts?.reduceMessages &&
            userIsInactive &&
            isValidSchemaVersion &&
            reducedMessages.length >= this.devOpts.reduceMessagesMinThreshold;

        const shouldWriteEntry = isValidSchemaVersion && (shouldWriteABTestAssignments || shouldWriteReducedMessages);

        try {
            if (shouldWriteEntry) {
                entry.rev += 1;

                const recoverySnapshotUpdate = updateSnapshotTimestamps({ entry, now: this.dateNow() });

                updateRecentlyReducedMessages(this.dateNow(), entry.metainfo, reducedMessages);

                const snapshot = virtual.snapshot();
                entry = snapshot.entry;

                // TODO: remove after completing user shared state items migration
                if (!hasMigratedLegacyUserSharedStateItems(entry)) {
                    const userSharedStateItems = Object.values(entry.userSharedStates).flatMap((items) =>
                        Object.values(items),
                    );

                    await this.db.sharedStates.migrateLegacyUserSharedStates(userSharedStateItems);
                }

                await this.db.writeEntryOrEntryDiff({
                    diff: snapshot.diff,
                    entry,
                    oldEntryDiffs: stateAndMessages.entryDiffs,
                    recoverySnapshotUpdate,
                    userId,
                });

                const messagesToDelete = [
                    ...reducedMessages,
                    ...previouslyReducedMessages,
                    ...getUnreducedMessagesForDeletion(this.dateNow(), unreducedMessages),
                ];

                // Write changeset and delete reduced messages after successfully writing entry:
                await Promise.all([
                    this.writeChangeset({
                        id: userId,
                        entry,
                        replication: { queue: [], results: [] },
                        messages: reducedMessages,
                        diff: snapshot.diff,
                        snapshotUpdate: recoverySnapshotUpdate,
                    }),

                    messagesToDelete.length > 0 ? this.db.deleteMessages(userId, messagesToDelete) : undefined,
                ]);
            }
        } catch (error: any) {
            // `revision does not match` errors can occur if another process writes the entry concurrently - ignore the error:
            const isConditionalWriteError = error instanceof ConditionalWriteError;

            if (!isConditionalWriteError) {
                const warning = new ReplicantError(
                    `User message reduction failed: ${error.message}`,
                    'server_error',
                    'db_error',
                    'warning',
                    { cause: error, receiverId: userId, reducedMessageLength: reducedMessages.length },
                );

                await this.reportError(warning);
            }
        }

        addNonEnumerableSystemStateFields(entry.state, getSystemStateFieldsFromEntry(userId, entry), this.config);

        return {
            consumedCapacity: stateAndMessages.consumedCapacity,
            entry: { ...entry, lastAppliedMessageId: reducedMessages.at(-1)?.id },
            isNewUser,
            messagesCount: reducedMessages.length,
        };
    };

    private async doIOSPostAPN(opts: {
        receiverId: string;
        receiverChatbotMetainfo: ChatbotMetainfo;
        notification: APNProps;
        payload: AnalyticsPayload;
        imageUrl?: string;
        analyticsUserProps?: { [key: string]: unknown };
    }): Promise<PushNotificationResponse> {
        const { receiverId, receiverChatbotMetainfo, notification, payload, imageUrl, analyticsUserProps } = opts;

        const deviceToken = receiverChatbotMetainfo.appleDeviceToken;
        if (!deviceToken) {
            return { success: false, reason: 'User not subscribed to APN' };
        }

        if (!receiverChatbotMetainfo.appleDeviceTokenUpdatedAt) {
            const now = this.dateNow();
            logger.info(`iOSPostAPN - Apple device token update time being set for: ${receiverId} to ${now}`);
            await this.db.chatbot.setAppleDeviceToken(receiverId, deviceToken, now);
        } else if (isAppleDeviceTokenExpired(receiverChatbotMetainfo.appleDeviceTokenUpdatedAt, this.dateNow())) {
            logger.info(`iOSPostAPN - Apple device token is considered expired for: ${receiverId}`);
            return { success: false, reason: 'User has been determined to no longer be subscribed to APN' };
        }

        const sessionId = receiverChatbotMetainfo.lastSessionId || 'sessionid-' + generateRandomId();

        const rawPayload: APNRawPayload = { aps: { ...notification }, payload: { ...payload } };

        if (imageUrl) {
            rawPayload.aps['mutable-content'] = 1;
            rawPayload.imageUrl = imageUrl;
        }

        return this.iosNotifications.sendIOSPushNotification({
            receiverId,
            appleDeviceToken: deviceToken,
            rawPayload,
            analyticsEventProperties: {
                timestamp: this.dateNow(),
                ...payload,
            },
            analyticsUserProperties: analyticsUserProps,
            userSessionId: sessionId,
        });
    }

    private async doAndroidPushNotification(opts: {
        receiverId: string;
        receiverChatbotMetainfo: ChatbotMetainfo;
        notification: FCMProps;
        payload: AnalyticsPayload;
        imageUrl?: string;
        analyticsUserProps?: { [key: string]: unknown };
    }): Promise<PushNotificationResponse> {
        const {
            receiverId,
            receiverChatbotMetainfo,
            notification: origNotification,
            payload,
            imageUrl,
            analyticsUserProps,
        } = opts;

        const deviceToken = receiverChatbotMetainfo.androidDeviceToken;

        if (!deviceToken) {
            return { success: false, reason: 'User not subscribed to FCM' };
        }

        const tokenUpdatedAt = receiverChatbotMetainfo.appleDeviceTokenUpdatedAt;
        const now = this.dateNow();

        if (!tokenUpdatedAt) {
            logger.info(
                `AndroidPushNotification - Android device token update time being set for: ${receiverId} to ${now}`,
            );
            await this.db.chatbot.setAndroidDeviceToken(receiverId, deviceToken, now);
        } else if (isAndroidDeviceTokenExpired(tokenUpdatedAt, now)) {
            logger.info(`AndroidPushNotification - Android device token is considered expired for: ${receiverId}`);
            return { success: false, reason: 'User has been determined to no longer be subscribed to Firebase' };
        }

        const sessionId = receiverChatbotMetainfo.lastSessionId || 'sessionid-' + generateRandomId();

        const notification: FCMProps = origNotification;

        if (imageUrl) {
            notification.image = imageUrl;
        }

        return this.androidNotifications.sendAndroidPushNotification({
            receiverId,
            androidDeviceToken: deviceToken,
            notification,
            payload,
            analyticsEventProperties: {
                timestamp: this.dateNow(),
                ...payload,
            },
            analyticsUserProperties: analyticsUserProps,
            userSessionId: sessionId,
        });
    }

    private async getExtraLoginData(ipAddress?: string): Promise<LoginInfo['extraData']> {
        const geolocation = ipAddress ? this.devOpts.lookupGeoIp(ipAddress) : undefined;

        const [ecpmPerAdPlacement, exchangeRates] = await Promise.all([
            this.platform === 'fb' && geolocation?.country ? this.ecpm.getECPM(geolocation.country) : undefined,
            this.exchangeRateHandler.getExchangeRates(),
        ]);

        let revenueCurrency: string | undefined;
        try {
            revenueCurrency = process.env.REVENUE_CURRENCY;
        } catch {}

        return {
            ecpmPerAdPlacement,
            exchangeRates,
            geolocation,
            amplitudeTimeZone: this.config.amplitudeTimeZone ?? 'UTC',
            revenueCurrency,
        };
    }

    pushDocsToIndex: PushDocsToIndexFn = async (docs) => {
        if (!this.supportsIndexing) {
            return;
        }

        const indexableDocs: { [userId: string]: { [key: string]: unknown } } = {};

        for (const userId in docs) {
            const isIndexableDoc = this.config.indexAnonymousWebPlayers || !isAnonymousWebPlayer(userId);

            if (isIndexableDoc) {
                const doc = docs[userId]!;
                indexableDocs[userId] = doc;

                // Report Sentry warning if index document size is close to the 256KB SQS message limit:
                const docSize = JSON.stringify(doc).length;
                if (docSize > 200_000) {
                    await this.reportError(createIndexDocTooLargeWarning({ docSize, userId }));
                }
            }
        }

        if (Object.keys(indexableDocs).length === 0) {
            return;
        }

        try {
            if (this.opts.indexingQueue) {
                await this.opts.indexingQueue.enqueueIndexDocuments(indexableDocs);
            } else {
                await this.opts.indexDB!.batchPushDocuments(indexableDocs);
            }
        } catch (e: any) {
            // Pushing to the index is not a fatal error and the client should continue to operate.
            // Log the error to Sentry and continue.
            await this.reportError(e);
        }
    };

    private pushSharedStateDocToIndex: PushSharedStateDocToIndexFn = async (opts) => {
        const { indexDoc, stateId, stateName } = opts;

        const sharedState = this.config.sharedStates?.[stateName];
        if (!sharedState) {
            throw Error('Cannot index invalid shared state with name: ' + stateName);
        }

        const indexDB = this.opts.sharedStateIndexDBs?.[stateName];
        if (!indexDB) {
            return;
        }

        // Report Sentry warning if index document size is close to the 256KB SQS message limit:
        const docSize = JSON.stringify(indexDoc).length;
        if (docSize > 200_000) {
            await this.reportError(createIndexDocTooLargeWarning({ docSize, sharedStateId: stateId }));
        }

        try {
            if (this.opts.indexingQueue) {
                await this.opts.indexingQueue.enqueueSharedStateIndexDocument(stateName, stateId, indexDoc);
            } else {
                await indexDB.batchPushDocuments({ [stateId]: indexDoc });
            }
        } catch (e: any) {
            // Pushing to the index is not a fatal error and the client should continue to operate.
            // Log the error to Sentry and continue.
            await this.reportError(e);
        }
    };

    /** Converts `entry` into a VirtualEntry and returns that. */
    private migrateAndApplyMessages: MigrateAndApplyMessagesFn = (id, entry, messages, opts) => {
        try {
            // Migrate and apply any defaults to the state.
            const virtual = entry instanceof VirtualEntry ? entry : createVirtualEntry(this.config, entry);

            this.sharedStates.migrateUserSharedStates(virtual.entry.userSharedStates);

            // Update AB test assignments before applying messages:
            if (opts?.abTestAssignments) {
                updateABTestAssignments(virtual.entry, {
                    newAssignments: opts.abTestAssignments,
                    isNewUser: virtual.entry.rev === 0,
                });
            }

            // Apply messages.
            const { reducedMessages, unreducedMessages } = this.applyMessagesToState(id, virtual.entry, messages, {
                isSampled: opts?.isSampled,
            });

            return { virtual, reducedMessages, unreducedMessages };
        } catch (e) {
            if (this.isInitialDevelopment()) {
                // During initial development, it's ok to return default states.
                const virtual =
                    entry instanceof VirtualEntry
                        ? entry
                        : createVirtualEntry(this.config, {
                              ...entry,
                              state: this.stateSchema.getDefault() as UnknownState,
                              lastUpdated: Date.now(),
                          });

                return { virtual, reducedMessages: [], unreducedMessages: [] };
            } else {
                throw e;
            }
        }
    };

    private writeChangeset: WriteChangesetFN = async (opts) => {
        await this.db.writeChangeset({
            ...opts,
            date: opts.date ?? new Date(),
            appVersion: this.config.version,
            snapshotUpdate: opts.snapshotUpdate,
        });
    };

    private applyMessagesToState: ApplyMessagesToStateFn = (id, userData, messages, opts) => {
        const reducedMessages: UserMessageItem[] = [];
        const unreducedMessages: UserMessageItem[] = [];

        const reportMessageWarning = (
            text: string,
            warningSubCode: ReplicantErrorSubCode,
            message: UserMessageItem,
        ): void => {
            // A message becomes old 23 hours after it's sent.
            const isOldMessage = this.dateNow() - message.timestamp > duration({ hours: 23 });
            if (!isOldMessage) {
                // For new messages, assume this is an old version the client is playing on.
                // A fix is probably already released and this will get reduced next time.
                return;
            }

            // A fix was not released. Report the warning.
            const context = { message, receiver: id, state: userData.state };
            const warning = new ReplicantError(text, 'server_error', warningSubCode, 'warning', context);

            void this.errorReporter.captureException(warning); // TODO await to prevent losing warning reports
        };

        const applicableMessages = getApplicableMessages(this.messages, messages, {
            onUnknownMessage: (message) => {
                unreducedMessages.push(message);

                const messageName = 'name' in message ? message.name : message.event.type;

                reportMessageWarning(
                    `Unknown message: ${messageName}. This can happen when a user on an old client version tries to read the state of a user on a newer client version.`,
                    'unknown_message',
                    message,
                );
            },
            onInvalidArgs: (message, validationError) => {
                unreducedMessages.push(message);

                const messageName = 'name' in message ? message.name : message.event.type;

                reportMessageWarning(
                    `Error validating args for message '${messageName}': ${validationError}. This can happen when a user on an old client version tries to read the state of a user on a newer client version.`,
                    'invalid_message_args',
                    message,
                );
            },
        }).filter((m) => !isMessageRecentlyReduced(userData.metainfo, m));

        if (applicableMessages.length > 0) {
            addNonEnumerableSystemStateFields(userData.state, getSystemStateFieldsFromEntry(id, userData), this.config);

            // Validate that user state is still valid after message application:
            const validateUserState = () => this.stateSchema.tryValidate(userData.state);

            for (const message of applicableMessages) {
                try {
                    if (isEventHandlerMessage(message)) {
                        applyEventHandlerMessage(userData, message, validateUserState);
                    } else {
                        const { reducer } = getNamespacedValue(this.messages as Messages<any>, message.name)!;

                        const performanceContext: perf.PerformanceContext = {
                            type: 'message',
                            name: message.name,
                            userId: id,
                        };

                        const wrappedReducer = opts?.isSampled
                            ? perf.withPerformanceTracking(reducer, performanceContext)
                            : reducer;

                        revertMutationIfThrows(userData.state, () => {
                            wrappedReducer(userData.state, message.args, {
                                senderId: message.sender,
                                timestamp: message.timestamp,
                            });

                            validateUserState();
                        });
                    }

                    reducedMessages.push(message);
                } catch (e: any) {
                    const messageName = isEventHandlerMessage(message) ? message.event.type : message.name;

                    const warning =
                        e instanceof SB.ValidationError
                            ? new ReplicantError(
                                  `Applying message ${messageName} resulted in invalid state`,
                                  'replication_error',
                                  'invalid_state_after_message',
                                  'warning',
                                  { ...message, cause: e.message },
                              )
                            : new ReplicantError(
                                  `Error applying message ${messageName}: ${e}`,
                                  'replication_error',
                                  'message_errored',
                                  'warning',
                                  { ...message, receiver: id, receiverRev: userData.rev },
                              );

                    // Preserve the message embedded in the stack, but override the stack itself.
                    const [, ...originalStackLines] = e.stack.split('\n');
                    const messageLine = warning.stack?.split('\n')[0];
                    warning.stack = [messageLine, ...originalStackLines].join('\n');

                    void this.errorReporter.captureException(warning); // TODO await to prevent losing error reports

                    // Do not retry event handler messages with inapplicable state diff.
                    // State may have changed since the event invocation so that event state diff is no longer applicable.
                    const isRetriableMessage = !(
                        isEventHandlerMessage(message) &&
                        e instanceof ReplicantError &&
                        e.subCode === 'inapplicable_diff'
                    );

                    if (isRetriableMessage) {
                        unreducedMessages.push(message);
                    } else {
                        reducedMessages.push(message);
                    }
                }
            }
        }

        return { reducedMessages, unreducedMessages };
    };

    private createActionAPI: CreateActionAPIFn<T> = (opts) => {
        const { async, consistentFetchIds, delayedActions, entry, invokeTime, sessionId, userId, fetcher } = opts;

        const scheduledActionsApi = createScheduledActionsAPI({
            errorReporter: this.errorReporter,
            userId,
            userData: entry,
            db: this.db,
            delayedActions,
            scheduledActions: this.config.scheduledActions ?? {},
            invokeTime,
        });

        let createChatbotApi: typeof createPlatformChatbotAPI;
        if (
            this.opts.platform === 'fb' ||
            this.opts.platform === 'line' ||
            this.opts.platform === 'zoom' ||
            this.opts.platform === 'web'
        ) {
            createChatbotApi = createPlatformChatbotAPI;
        } else {
            createChatbotApi = createStubChatbotAPI;
        }

        const chatbotApi = createChatbotApi({
            apiAccess: this,
            richMenuAPIClient: this.devOpts.richMenuAPIClient,
            userId,
            db: this.db,
            entry,
            errorReporter: this.errorReporter,
            sendChatbotMessage: (...args) => this.chatbot.sendChatbotMessage(...args).then((result) => result.ok),
            makeLineMessageReservation: this.lineMessageClient
                ? () => this.lineMessageClient!.reserve()
                : () => {
                      throw new Error('Not available on non-LINE platforms');
                  },
            sendLineChatbotMessage: (...args) =>
                this.chatbot.sendLineChatbotMessage(...args).then((result) => result.ok),
            sendInstagramChatbotMessage: (...args) => this.chatbot.sendInstagramChatbotMessage(...args),
            sendMessengerChatbotMessage: (...args) => this.chatbot.sendMessengerChatbotMessage(...args),
            sendTelegramChatbotMessage: (...args) => this.chatbot.sendTelegramChatbotMessage(...args),
            delayedActions,
            invokeTime,
            validateChatbotSubscription: this.devOpts.validateChatbotSubscription,
            fetcher,
        });

        return this.opts.createActionAPI({
            abTestsDynamicConfig: opts.abTestsDynamicConfig,
            apiAccess: this,
            async,
            chatbotApi,
            consistentFetchIds,
            db: this.db,
            delayedActions,
            encryptUserId: (userId) => this.userIdEncryption.encryptUserId(userId),
            entry,
            id: userId,
            invokeTime,
            komojuClient: this.opts.komojuClient,
            telegramClient: this.opts.telegramClient,
            xsollaClient: this.opts.xsollaClient,
            messages: this.config.messages,
            platform: this.opts.platform,
            scheduledActionsApi,
            sendTemplatedEmail: this.devOpts.sendTemplatedEmail,
            sessionId,
            sharedStates: this.config.sharedStates,
            userAssetsBaseUrl: this.devOpts.userAssetsBaseUrl,
            fetcher,
        });
    };

    createChatbotRendererApi: CreateChatbotRendererAPIFn = ({ receiverId, senderId }) => {
        const assets = this.config.chatbot?.assets;
        const { chatbotAssetUrls, userAssetsBaseUrl } = this.devOpts;

        return {
            getAssetPath: (x: string) => {
                if (!assets) {
                    throw new ReplicantError(
                        'Chatbot assets not configured.',
                        'server_error',
                        'chatbot_missing_configuration',
                        'fatal',
                    );
                }

                if (!assets[x as keyof typeof assets]) {
                    throw new ReplicantError(
                        'Asset ' + x.toString() + ' not found.',
                        'server_error',
                        'chatbot_missing_asset',
                        'fatal',
                    );
                }

                if (!chatbotAssetUrls[x]) {
                    throw new ReplicantError(
                        'No entry for ' + x.toString() + ' in chatbotAssetUrls',
                        'server_error',
                        'chatbot_missing_asset_url',
                        'fatal',
                    );
                }

                return chatbotAssetUrls[x]!;
            },

            getSenderID: () => senderId,
            getReceiverID: () => receiverId,
            getUserAssetUrl: (key: string) => userAssetsBaseUrl + key,
        };
    };

    createEventHandlerApi: CreateEventHandlerAPIFn<T> = async (opts) => {
        const { abTests: abTestsDynamicConfig } = await this.abTests.getDynamicConfig();

        const { getSessionID, ...api } = this.createActionAPI({
            abTestsDynamicConfig,
            async: true,
            consistentFetchIds: opts.consistentFetchIds,
            delayedActions: opts.delayedActions,
            entry: opts.entry,
            invokeTime: this.dateNow(),
            sessionId: '', // The session will not be accessible.
            userId: opts.userId,
            fetcher: this.opts.fetcher,
            isServerSide: false,
        });

        // Exclude validatePurchase from the runtime value
        delete (api.purchases as Partial<typeof api.purchases>).validatePurchase;

        api.math.random = Math.random;

        return api;
    };

    private createPayloadEncoderAPI(opts: {
        userId: string;
        sessionId: string;
        receiverId: string;
        delayedActions: DelayedAction[];
    }): ReplicantPlatformMessagePayloadEncoderAPI {
        return {
            date: { now: this.dateNow },
            math: { random: () => Math.random() },

            kvStore: this.kvStore,

            getUserID: () => opts.userId,
            getSessionID: () => opts.sessionId,
            getReceiverID: () => opts.receiverId,

            ...createReportingAPIs({
                apiAccess: this,
                delayedActions: opts.delayedActions,
                id: opts.userId,
                sessionId: opts.sessionId,
            }),
        };
    }

    private handler<TArgs extends any[], TResult>(fn: (...args: TArgs) => Promise<TResult>) {
        return async (...args: TArgs) => {
            if (this.devOpts.simulatedLatency) {
                await delay(this.devOpts.simulatedLatency / 2);
            }

            const tStart = this.dateNow();

            let data;
            try {
                data = await fn(...args);
            } catch (e) {
                logger.error(e);
                throw e;
            }

            const tEnd = this.dateNow();

            if (this.devOpts.simulatedLatency) {
                await delay(this.devOpts.simulatedLatency / 2);
            }

            return { data, t: tStart, service: tEnd - tStart };
        };
    }

    isInitialDevelopment(): boolean {
        let isDev: boolean;
        try {
            // This code gets executed on the client as well, which is not guaranteed to have process.env defined.
            isDev = process.env.STAGE !== 'prod';
        } catch (e) {
            isDev = false;
        }
        return isDev && this.config.migrator.getLatestVersion() === 1;
    }
}
